/**
 * Доски, столбцы, участники.
 *
 * Ключевая деталь §8.3: доска знает свой режим привязки проекта.
 * BOARD — проект на доске, столбцы без проекта; COLUMN — наоборот.
 * Одновременно оба заполнены быть не могут (инвариант §20.3).
 */

import type {
  BoardRoleCode,
  Permission,
  ProjectBindingMode,
  StatusSource,
} from "@taskflow/contracts";

import { api } from "../api";

export interface BoardSummary {
  id: string;
  name: string;
  departmentId: string;
  /** Код подразделения: базовые OA, OOA, OAP или заведённый администратором. */
  departmentCode: string;
  projectBindingMode: ProjectBindingMode;
  /** Проект доски — только в режиме BOARD. */
  projectId?: string | null;
  projectName: string | null;
  /** AC-08: рядом с названием доски — краткое имя проекта. */
  projectShortName: string | null;
  statusSource: StatusSource;
  /** «Личная (без проекта)»: только создатель, вне отчётности. */
  isPersonal: boolean;
  boardRole: BoardRoleCode;
  /** Права пользователя на ЭТОЙ доске — объединяются с системными (§6.1). */
  permissions: Permission[];
  taskCount: number;
  /** Доска в архиве с этого момента; null — активная. */
  archivedAt: string | null;
  version: number;
}

/** Архивная доска в списке «Архив досок». */
export interface ArchivedBoard extends BoardSummary {
  archivedAt: string;
  archivedByName: string | null;
  /** Задачи, ушедшие в архив вместе с доской, — вернутся при восстановлении. */
  archivedTaskCount: number;
}

export interface BoardColumn {
  id: string;
  name: string;
  position: number;
  /** Заполнен только в режиме COLUMN. */
  projectId: string | null;
  projectName: string | null;
  /** «Информация» о проекте столбца; своя у каждой доски. Только режим COLUMN. */
  projectInfo: string | null;
  statusCategory: string | null;
  requireAssigneeOnEntry: boolean;
  /** Разрешённые переходы из этого столбца (§12.3). */
  allowedTargets: string[];
  isActive: boolean;
  version: number;
}

export interface BoardDetail extends BoardSummary {
  description: string | null;
  columns: BoardColumn[];
  createColumnId: string;
  /** Задачи в столбце выстраиваются по системному статусу. Только режим COLUMN. */
  sortByStatus: boolean;
  /** «Отметка о просмотре»: просмотренные задачи закрашиваются цветом. Только режим COLUMN. */
  viewMarkEnabled: boolean;
  /** Личный цвет отметки смотрящего, #RRGGBB; null — ещё не выбран. */
  viewMarkColor: string | null;
}

export interface BoardMember {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  position: string | null;
  avatarUrl: string | null;
  boardRoleId: string;
  /** Код роли: по нему, а не по идентификатору, узнаётся руководитель. */
  boardRoleCode: string;
  boardRoleName: string;
  joinedAtLabel: string;
  /** §25.4: сколько открытых задач потребует решения при удалении. */
  openAssignments: number;
}

export interface BoardRole {
  id: string;
  code: string;
  name: string;
}

export interface BoardInvitation {
  id: string;
  email: string;
  status: "PENDING" | "EXPIRED";
  sentAtLabel: string;
  expiresAtLabel: string;
  roleId: string | null;
  roleName: string;
}

export interface BoardTag {
  id: string;
  name: string;
  color: string | null;
  kind: "REGULAR" | "SYSTEM_STATUS";
  systemCode: string | null;
  status: "ACTIVE" | "ARCHIVED";
  version: number;
}

export const boardsApi = api.injectEndpoints({
  endpoints: (build) => ({
    boards: build.query<{ data: BoardSummary[] }, void>({
      query: () => ({ method: "boards.list" }),
      providesTags: ["BoardList"],
    }),

    board: build.query<BoardDetail, string>({
      query: (boardId) => ({ method: "boards.get", params: { boardId } }),
      providesTags: (_result, _error, boardId) => [{ type: "Board" as const, id: boardId }, "Board"],
    }),

    boardMembers: build.query<{ data: BoardMember[]; roles: BoardRole[] }, string>({
      query: (boardId) => ({ method: "boards.members.list", params: { boardId } }),
      providesTags: ["Members"],
    }),

    boardTags: build.query<{ data: BoardTag[] }, string>({
      query: (boardId) => ({ method: "boards.tags.list", params: { boardId } }),
      providesTags: ["Tags"],
    }),

    createBoard: build.mutation<
      BoardDetail,
      {
        name: string;
        description: string | null;
        departmentId: string;
        projectBindingMode: ProjectBindingMode;
        projectId: string | null;
        columns: { name: string; projectId: string | null; statusCategory: string | null }[];
        memberIds: string[];
        /** Личная доска: режим BOARD без проекта и без участников. */
        personal?: boolean;
        clientRequestId: string;
      }
    >({
      query: (params) => ({ method: "boards.create", params }),
      invalidatesTags: ["BoardList"],
    }),

    archivedBoards: build.query<{ data: ArchivedBoard[] }, void>({
      query: () => ({ method: "boards.archived.list" }),
      providesTags: ["BoardList"],
    }),

    // Вместе с доской в архив уходят и возвращаются её задачи: сбрасываются
    // и списки задач, и архив задач.
    archiveBoard: build.mutation<
      { id: string; archivedTasks: number },
      { boardId: string; version: number }
    >({
      query: (params) => ({ method: "boards.archive", params }),
      invalidatesTags: ["Board", "BoardList", "TaskList", "Archive"],
    }),

    restoreBoard: build.mutation<
      { id: string; restoredTasks: number },
      { boardId: string; version: number }
    >({
      query: (params) => ({ method: "boards.restore", params }),
      invalidatesTags: ["Board", "BoardList", "TaskList", "Archive"],
    }),

    // ── Настройки доски. Прежде ни одной из этих мутаций не было, и кнопки
    //    настроек показывали «подключается вместе с backend». ─────────────

    updateBoard: build.mutation<
      BoardDetail,
      {
        boardId: string;
        version: number;
        name?: string;
        description?: string | null;
        sortByStatus?: boolean;
        viewMarkEnabled?: boolean;
      }
    >({
      query: (params) => ({ method: "boards.update", params }),
      invalidatesTags: ["Board", "BoardList"],
    }),

    // Цвет отметки личный: доску не меняет и другим участникам не
    // рассылается. Кэш доски правится сразу — карточки перекрашиваются
    // без ожидания ответа, при отказе цвет возвращается.
    setViewMarkColor: build.mutation<BoardDetail, { boardId: string; color: string }>({
      query: (params) => ({ method: "boards.setViewMarkColor", params }),
      async onQueryStarted({ boardId, color }, lifecycleApi) {
        const patch = lifecycleApi.dispatch(
          boardsApi.util.updateQueryData("board", boardId, (draft) => {
            draft.viewMarkColor = color;
          }),
        );
        try {
          await lifecycleApi.queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    changeProjectModel: build.mutation<
      BoardDetail,
      {
        boardId: string;
        version: number;
        projectBindingMode: ProjectBindingMode;
        projectId?: string | null;
        /** Столбец → проект. Обязателен для каждого активного столбца в режиме COLUMN. */
        columnProjects?: Record<string, string>;
      }
    >({
      // §8.4: затрагивает доску, все столбцы и проект всех задач.
      query: (params) => ({ method: "boards.changeProjectModel", params }),
      invalidatesTags: ["Board", "BoardList", "Columns", "TaskList", "Task"],
    }),

    addMember: build.mutation<unknown, { boardId: string; userId: string; boardRoleId: string }>({
      query: (params) => ({ method: "boards.members.add", params }),
      invalidatesTags: ["Members", "Board"],
    }),

    setMemberRole: build.mutation<
      unknown,
      { boardId: string; memberId: string; boardRoleId: string }
    >({
      query: (params) => ({ method: "boards.members.setRole", params }),
      invalidatesTags: ["Members", "Board"],
    }),

    removeMember: build.mutation<
      { ok: boolean },
      { boardId: string; memberId: string; reassignTo?: string }
    >({
      // §25.4: задачи удаляемого переходят замене или снимаются — одной транзакцией.
      query: (params) => ({ method: "boards.members.remove", params }),
      invalidatesTags: ["Members", "Board", "TaskList", "Task"],
    }),

    boardInvitations: build.query<{ data: BoardInvitation[] }, string>({
      query: (boardId) => ({ method: "boards.invitations.list", params: { boardId } }),
      providesTags: ["Invitations"],
    }),

    inviteToBoard: build.mutation<
      { id: string; email: string },
      { boardId: string; email: string; boardRoleId: string; message?: string | null }
    >({
      query: (params) => ({ method: "boards.invitations.create", params }),
      invalidatesTags: ["Invitations"],
    }),

    resendInvitation: build.mutation<unknown, { boardId: string; inviteId: string }>({
      query: (params) => ({ method: "boards.invitations.resend", params }),
      invalidatesTags: ["Invitations"],
    }),

    revokeInvitation: build.mutation<unknown, { boardId: string; inviteId: string }>({
      query: (params) => ({ method: "boards.invitations.revoke", params }),
      invalidatesTags: ["Invitations"],
    }),

    createColumn: build.mutation<
      BoardColumn,
      {
        boardId: string;
        name: string;
        projectId?: string | null;
        statusCategory?: string | null;
        requireAssigneeOnEntry?: boolean;
      }
    >({
      query: (params) => ({ method: "boards.columns.create", params }),
      invalidatesTags: ["Columns", "Board"],
    }),

    updateColumn: build.mutation<
      BoardColumn,
      {
        boardId: string;
        columnId: string;
        version: number;
        name?: string;
        projectId?: string | null;
        projectInfo?: string;
        statusCategory?: string;
        requireAssigneeOnEntry?: boolean;
      }
    >({
      query: (params) => ({ method: "boards.columns.update", params }),
      // Смена статуса столбца меняет статус и историю его задач.
      invalidatesTags: ["Columns", "Board", "Task", "Events"],
    }),

    deleteColumn: build.mutation<
      unknown,
      { boardId: string; columnId: string; targetColumnId: string }
    >({
      // §21.6: задачи удаляемого столбца переносятся — они не пропадают.
      query: (params) => ({ method: "boards.columns.delete", params }),
      invalidatesTags: ["Columns", "Board", "TaskList"],
    }),

    createTag: build.mutation<BoardTag, { boardId: string; name: string; color?: string | null }>({
      query: (params) => ({ method: "boards.tags.create", params }),
      invalidatesTags: ["Tags"],
    }),

    updateTag: build.mutation<
      BoardTag,
      { boardId: string; tagId: string; version: number; name?: string; color?: string | null }
    >({
      query: (params) => ({ method: "boards.tags.update", params }),
      invalidatesTags: ["Tags", "TaskList"],
    }),

    archiveTag: build.mutation<BoardTag, { boardId: string; tagId: string }>({
      query: (params) => ({ method: "boards.tags.archive", params }),
      invalidatesTags: ["Tags"],
    }),

    restoreTag: build.mutation<BoardTag, { boardId: string; tagId: string }>({
      query: (params) => ({ method: "boards.tags.restore", params }),
      invalidatesTags: ["Tags"],
    }),

    reorderColumns: build.mutation<{ ok: boolean }, { boardId: string; columnIds: string[] }>({
      // §21.6: сервер принимает полный массив в новом порядке, а не пару
      // «что и куда» — при гонке порядок не разъедется.
      query: (params) => ({ method: "boards.columns.reorder", params }),
      invalidatesTags: ["Columns", "Board"],
    }),
  }),
});

export const {
  useBoardsQuery,
  useBoardQuery,
  useBoardMembersQuery,
  useBoardTagsQuery,
  useCreateBoardMutation,
  useReorderColumnsMutation,
  useUpdateBoardMutation,
  useSetViewMarkColorMutation,
  useChangeProjectModelMutation,
  useAddMemberMutation,
  useSetMemberRoleMutation,
  useRemoveMemberMutation,
  useBoardInvitationsQuery,
  useInviteToBoardMutation,
  useResendInvitationMutation,
  useRevokeInvitationMutation,
  useCreateColumnMutation,
  useUpdateColumnMutation,
  useDeleteColumnMutation,
  useCreateTagMutation,
  useUpdateTagMutation,
  useArchiveTagMutation,
  useRestoreTagMutation,
  useArchivedBoardsQuery,
  useArchiveBoardMutation,
  useRestoreBoardMutation,
} = boardsApi;
